import { LoggerService } from "@bfx/logger";
const logger = LoggerService.for(__dirname);

import { Injectable } from "@angular/core";
import {
  PRIMITIVE,
  ServiceMessageBrokerFactory,
  ServiceMessageBroker,
} from "shared/provider-webworker";
import { IVwapService, CHANNEL_VWAP_SERVICE, Vwap } from "../IVwapService";

import * as CubiqTS from "shared/cubiq-model-multi-asset";
import { OrderDataLookupService } from "./../../lookup/model/impl/OrderDataLookupService";
import { CcyPairLookupService } from "./../../lookup/model/impl/CcyPairLookupService";
import { CcyLookupService } from "./../../lookup/model/impl/CcyLookupService";
import {
  AlgoClientTradeLookupService,
  BMClientTradeLookupService,
  LimitClientTradeLookupService,
} from "./../../lookup/model/impl/TradeDealLookupService";
import { PairVwap } from "../../model/core/PairVwap";
import { RateCache } from "../../rate/impl/RateCache";

import { BasePricingService } from "shared/cubiq-services/lookup/pricing/impl/BasePricingService";
import {
  LimitRiskTradeLookupService,
  AlgoRiskTradeLookupService,
  BMRiskTradeLookupService,
} from "../../lookup/model/impl/RiskTradeLookupService";
import { BaseLookupService } from "../../lookup/impl/BaseLookupService";

const ZERO: CubiqTS.BigNumber = new CubiqTS.BigNumber("0");
const PAIRNAME_MULTIPLE = "MULTIPLE";
@Injectable()
export class VwapPrimaryService implements IVwapService {
  protected broker: ServiceMessageBroker;
  protected services: BaseLookupService<string, CubiqTS.DealImpl>[];

  constructor(
    protected factory: ServiceMessageBrokerFactory,
    private ccyLookupService: CcyLookupService,
    private ccyPairLookupService: CcyPairLookupService,
    private orderLookupService: OrderDataLookupService,
    private limitClientLookupService: LimitClientTradeLookupService,
    private algoClientLookupService: AlgoClientTradeLookupService,
    private bmClientLookupService: BMClientTradeLookupService,
    private limitRiskLookupService: LimitRiskTradeLookupService,
    private algoRiskLookupService: AlgoRiskTradeLookupService,
    private bmRiskLookupService: BMRiskTradeLookupService,
    private rateCache: RateCache,
    private pricingService: BasePricingService,
  ) {
    this.broker = this.factory.createMessageBroker(CHANNEL_VWAP_SERVICE, true, true);
    this.broker.registerMethod(
      "getVwapForOrders",
      [PRIMITIVE],
      this.getVwapForOrders.bind(this),
      PRIMITIVE,
    );
    this.broker.registerMethod(
      "getVwapForTradeDeals",
      [PRIMITIVE],
      this.getVwapForTradeDeals.bind(this),
      PRIMITIVE,
    );
    this.services = [
      this.limitClientLookupService,
      this.algoClientLookupService,
      this.bmClientLookupService,
      this.limitRiskLookupService,
      this.algoRiskLookupService,
      this.bmRiskLookupService,
    ];
  }

  public getVwapForOrders(orderIds: Array<string>, ndfMode: boolean = false): Promise<Vwap> {
    return new Promise<Vwap>((res, rej) => {
      let selecteOrders: Array<CubiqTS.OrderDataImpl> =
        this.orderLookupService.getOrderDataForIds(orderIds);
      let pairVwaps: Map<string, PairVwap> = new Map();
      let pairAmts: Map<string, CubiqTS.BigNumber> = new Map();
      let pairNetPositionByRate: Map<string, CubiqTS.BigNumber> = new Map();

      let netVWap = new PairVwap(PAIRNAME_MULTIPLE, 0, 0, 0, 0, false, "Spot");
      let overAllNetAmt: CubiqTS.BigNumber = ZERO;
      let overallNetPos: CubiqTS.BigNumber = ZERO;

      for (let order of selecteOrders) {
        if (CubiqTS.OrderType.LIMIT_CALL == order.orderType) {
          // NOP
        } else {
          let pairVwap: PairVwap = pairVwaps.get(order.baseCode + order.nonBaseCode);
          if (!pairVwap) {
            pairVwap = this.initPairVwap(order.baseCode, order.nonBaseCode, ndfMode);

            if (CubiqTS.RequestTrackingType.OUTRIGHT_RATE === order.requestTrackingType) {
              pairVwap.isFwdTracked = true;
            }

            pairVwaps.set(order.baseCode + order.nonBaseCode, pairVwap);

            if (netVWap.minRatePrecision < pairVwap.minRatePrecision) {
              netVWap.minRatePrecision = pairVwap.minRatePrecision;
            }
            if (netVWap.sptRatePrecision < pairVwap.sptRatePrecision) {
              netVWap.sptRatePrecision = pairVwap.sptRatePrecision;
            }
            if (netVWap.fwdRatePrecision < pairVwap.fwdRatePrecision) {
              netVWap.fwdRatePrecision = pairVwap.fwdRatePrecision;
            }
            if (netVWap.pipIndex < pairVwap.pipIndex) {
              netVWap.pipIndex = pairVwap.pipIndex;
            }
          }

          let pairAmt: CubiqTS.BigNumber = pairAmts.get(order.baseCode + order.nonBaseCode);
          if (!pairAmt) {
            pairAmt = ZERO;
            pairAmts.set(order.baseCode + order.nonBaseCode, pairAmt);
          }

          let orderAmt: CubiqTS.BigNumber = this.getOrderAmt(order);
          if (orderAmt) {
            pairAmt = pairAmt.plus(orderAmt);
            pairAmts.set(order.baseCode + order.nonBaseCode, pairAmt);

            overAllNetAmt = overAllNetAmt.plus(orderAmt);
          }

          let pairNetPos: CubiqTS.BigNumber = pairNetPositionByRate.get(
            order.baseCode + order.nonBaseCode,
          );
          if (!pairNetPos) {
            pairNetPos = ZERO;
            pairNetPositionByRate.set(order.baseCode + order.nonBaseCode, pairNetPos);
          }

          let orderNetPos: CubiqTS.BigNumber = this.getOrderNePosRate(order, ndfMode);
          if (orderNetPos) {
            pairNetPos = pairNetPos.plus(orderNetPos);
            pairNetPositionByRate.set(order.baseCode + order.nonBaseCode, pairNetPos);

            overallNetPos = overallNetPos.plus(orderNetPos);
          }
        }
      }

      let retCandidates: Array<PairVwap> = Array.from(pairVwaps.values());

      let retArr: Array<PairVwap> = [];
      for (let vwap of retCandidates) {
        // this.rate = this.myNetPositionByRate / this.amount;
        let netPosRate = pairNetPositionByRate.get(vwap.pairName);
        let amtVal = pairAmts.get(vwap.pairName);
        if (ZERO.eq(amtVal)) {
          // no amount
        } else {
          let rate = netPosRate.dividedBy(amtVal);
          this.formatVwapForOrders(vwap, netPosRate, amtVal, rate);
          retArr.push(vwap);
        }
      }

      if (ZERO.eq(overAllNetAmt)) {
        netVWap = this.formatVwapForOrders(netVWap, overallNetPos, overAllNetAmt, ZERO);
      } else {
        let netRate = overallNetPos.dividedBy(overAllNetAmt);
        netVWap = this.formatVwapForOrders(netVWap, overallNetPos, overAllNetAmt, netRate);
      }

      let ret: Vwap = new Vwap();
      ret.pairVWaps = retArr;
      ret.netVwap = netVWap;
      res(ret);
    });
  }
  formatVwapForOrders(
    vwap: PairVwap,
    netPosRate: CubiqTS.BigNumber,
    amtVal: CubiqTS.BigNumber,
    rate: CubiqTS.BigNumber,
  ): PairVwap {
    // https://www.bcdfx.com/ijira/browse/OMS-8579
    // cant have negative rates, this is an edge case
    if (rate.isLessThan(ZERO)) {
      rate = ZERO;
    }
    vwap.rate = this.getFormattedRate(vwap, rate);

    if (vwap.useOzs) {
      vwap.amount = amtVal.abs().toFormat(2) + " Oz";
    } else {
      vwap.amount = amtVal.abs().dividedBy(1e6).toFormat(2);
    }
    vwap.exactAmount = amtVal.abs().toFormat(0); // no pennies
    vwap.intent = amtVal.isLessThanOrEqualTo(ZERO) ? "Sell" : "Buy";

    return vwap;
  }

  public getVwapForTradeDeals(tradeDealIds: Array<string>): Promise<Vwap> {
    let selectedDeals: Array<CubiqTS.DealImpl> = this.getTradeDealsForIds(tradeDealIds);
    return this.getVwapForSelectedDeals(selectedDeals);
  }

  public getVwapForSelectedDeals(selectedDeals: Array<CubiqTS.DealImpl>): Promise<Vwap> {
    return new Promise<Vwap>((res, rej) => {
      let pairVwaps: Map<string, PairVwap> = new Map();
      let pairAmts: Map<
        string,
        {
          amt: CubiqTS.BigNumber;
          counterAmt: CubiqTS.BigNumber;
        }
      > = new Map();

      let netVWap = new PairVwap(PAIRNAME_MULTIPLE, 0, 0, 0, 0, false, "Spot");
      let overallNetAmt: any = {
        amt: ZERO,
        counterAmt: ZERO,
      };

      for (let deal of selectedDeals) {
        if (CubiqTS.OrderType.LIMIT_CALL == deal.orderType) {
          // NOP
        } else {
          let pairKey = deal.baseCcyCd + deal.nonBaseCcyCd;
          let pairVwap: PairVwap = pairVwaps.get(pairKey);

          if (!pairVwap) {
            pairVwap = this.initPairVwap(deal.baseCcyCd, deal.nonBaseCcyCd);
            pairVwaps.set(pairKey, pairVwap);

            if (netVWap.minRatePrecision < pairVwap.minRatePrecision) {
              netVWap.minRatePrecision = pairVwap.minRatePrecision;
            }
            if (netVWap.sptRatePrecision < pairVwap.sptRatePrecision) {
              netVWap.sptRatePrecision = pairVwap.sptRatePrecision;
            }
            if (netVWap.fwdRatePrecision < pairVwap.fwdRatePrecision) {
              netVWap.fwdRatePrecision = pairVwap.fwdRatePrecision;
            }
            if (netVWap.pipIndex < pairVwap.pipIndex) {
              netVWap.pipIndex = pairVwap.pipIndex;
            }
          }

          let pairAmt: any = pairAmts.get(pairKey);
          if (!pairAmt) {
            pairAmt = {
              amt: ZERO,
              counterAmt: ZERO,
            };
            pairAmts.set(pairKey, pairAmt);
          }

          let fillAmt: any = this.getFillAmt(deal);
          if (fillAmt) {
            pairAmt.amt = pairAmt.amt.plus(fillAmt.amt);
            pairAmt.counterAmt = pairAmt.counterAmt.plus(fillAmt.counterAmt);
            pairAmts.set(pairKey, pairAmt);

            overallNetAmt.amt = overallNetAmt.amt.plus(fillAmt.amt);
            overallNetAmt.counterAmt = overallNetAmt.counterAmt.plus(fillAmt.counterAmt);
          }
        }
      }

      pairVwaps.forEach((pairVwap, pairKey) => {
        let pairAmt = pairAmts.get(pairKey);
        this.calcVwapRateForTradeDeals(pairVwap, pairAmt);
      });

      let pairVwapsArr: Array<PairVwap> = Array.from(pairVwaps.keys())
        .sort()
        .map((sortedKey) => {
          return pairVwaps.get(sortedKey);
        });

      netVWap = this.calcVwapRateForTradeDeals(netVWap, overallNetAmt);

      let ret: Vwap = new Vwap();
      ret.pairVWaps = pairVwapsArr;
      ret.netVwap = netVWap;

      res(ret);
    });
  }

  calcVwapRateForTradeDeals(
    pairVwap: PairVwap,
    pairAmt: { amt: CubiqTS.BigNumber; counterAmt: CubiqTS.BigNumber },
  ): PairVwap {
    if (!ZERO.isEqualTo(pairAmt.amt)) {
      let rate: CubiqTS.BigNumber = pairAmt.counterAmt.dividedBy(pairAmt.amt);

      if (rate.comparedTo(ZERO) < 0) {
        rate = new CubiqTS.BigNumber("-1").times(rate);
      }

      if (rate.comparedTo(ZERO) < 0) {
        rate = ZERO;
      }

      pairVwap.rate = this.getFormattedRate(pairVwap, rate);
    } else {
      // template won't show at symbol for this
      pairVwap.rate = "0.00";
    }

    // FIXME
    if (!ZERO.isEqualTo(pairAmt.counterAmt)) {
      let rate: CubiqTS.BigNumber = pairAmt.amt.dividedBy(pairAmt.counterAmt);

      if (rate.comparedTo(ZERO) < 0) {
        rate = new CubiqTS.BigNumber("-1").times(rate);
      }

      if (rate.comparedTo(ZERO) < 0) {
        rate = ZERO;
      }

      pairVwap.invertRate = this.getFormattedRate(pairVwap, rate);
    }
    if (pairVwap.useOzs) {
      pairVwap.amount = pairAmt.amt.abs().toFormat(2) + " Oz";
    } else {
      pairVwap.amount = pairAmt.amt.abs().dividedBy(1e6).toFormat(2);
    }
    pairVwap.exactAmount = pairAmt.amt.abs().toFormat(0); // no pennies
    pairVwap.intent = pairAmt.amt.isLessThanOrEqualTo(ZERO) ? "Sell" : "Buy";

    return pairVwap;
  }

  getTradeDealsForIds(tradeDealIds: string[]): CubiqTS.DealImpl[] {
    return Array.isArray(tradeDealIds)
      ? tradeDealIds.reduce((deals, id) => {
          this.services.some((service) => {
            const deal = service.getItem(id);
            if (deal != null) {
              deals.push(deal);
              return true;
            }
            return false;
          });

          return deals;
        }, [])
      : [];
  }

  private initPairVwap(base: string, nonBase: string, ndfMode: boolean = false): PairVwap {
    let ccyPair = this.ccyPairLookupService.getCcyPair(base, nonBase);
    let minPrecision: number = ccyPair.rateDecimalsQty;
    let sptPrecision: number = Math.max(ccyPair.limitEntrySpotDecimals, ccyPair.markupSptDecimals);
    let fwdPrecision: number = ccyPair.fwdRateDecimalsQty;
    let pipIdx: number = ccyPair.pipsQty;
    let useOzs: boolean =
      this.ccyLookupService.getCcy(base).positionUnitId == CubiqTS.PositionUnit.Oz;

    let tenor: string = "Spot";
    if (ndfMode === true && ccyPair.ndfEnabledFg && ccyPair.ndfHorizonCd != null) {
      switch (ccyPair.ndfHorizonCd) {
        case CubiqTS.NdfHorizon._1MO:
          tenor = "1M";
          break;
        case CubiqTS.NdfHorizon.FirstFuture:
          tenor = "1st Fut";
          break;
      }
    }

    return new PairVwap(
      base + nonBase,
      minPrecision,
      sptPrecision,
      fwdPrecision,
      pipIdx,
      useOzs,
      tenor,
    );
  }

  private getFillAmt(deal: CubiqTS.DealImpl): any {
    let dealAmt = {
      amt: ZERO,
      counterAmt: ZERO,
    };

    let buy: boolean = deal.isBuy;
    let sell: boolean = !deal.isBuy;
    let dealtIsBase: boolean = deal.baseCcyCd === deal.dealtCcy;
    let invert: boolean = deal.productId === deal.nonBaseCcyCd + deal.baseCcyCd;

    if (!invert) {
      if (dealtIsBase && buy) {
        dealAmt.amt = deal.amt1;
        dealAmt.counterAmt = new CubiqTS.BigNumber("-1").times(deal.amt2);
      } else if (dealtIsBase && sell) {
        dealAmt.amt = new CubiqTS.BigNumber("-1").times(deal.amt1);
        dealAmt.counterAmt = deal.amt2;
      } else if (!dealtIsBase && buy) {
        dealAmt.amt = new CubiqTS.BigNumber("-1").times(deal.amt2);
        dealAmt.counterAmt = deal.amt1;
      } else if (!dealtIsBase && sell) {
        dealAmt.amt = deal.amt2;
        dealAmt.counterAmt = new CubiqTS.BigNumber("-1").times(deal.amt1);
      }
    } else {
      if (dealtIsBase && buy) {
        dealAmt.amt = deal.amt1;
        dealAmt.counterAmt = new CubiqTS.BigNumber("-1").times(deal.amt2);
      } else if (dealtIsBase && sell) {
        dealAmt.amt = new CubiqTS.BigNumber("-1").times(deal.amt1);
        dealAmt.counterAmt = deal.amt2;
      } else if (!dealtIsBase && buy) {
        dealAmt.amt = new CubiqTS.BigNumber("-1").times(deal.amt2);
        dealAmt.counterAmt = deal.amt1;
      } else if (!dealtIsBase && sell) {
        dealAmt.amt = deal.amt2;
        dealAmt.counterAmt = new CubiqTS.BigNumber("-1").times(deal.amt1);
      }
    }

    return dealAmt;
  }

  private getOrderAmt(order: CubiqTS.OrderDataImpl): CubiqTS.BigNumber {
    if (order.orderType != CubiqTS.OrderType.LIMIT_CALL) {
      if (this.amBuyingBase(order)) {
        if (order.fixedCode == order.nonBaseCode) {
          return order.leavesSettleQty;
        }
        return order.leavesQty;
      } else {
        if (order.fixedCode == order.nonBaseCode) {
          return order.leavesSettleQty.times(-1);
        }
        return order.leavesQty.times(-1);
      }
    }
    return null;
  }

  private getOrderNePosRate(order: CubiqTS.OrderDataImpl, ndfMode: boolean): CubiqTS.BigNumber {
    if (order.orderType != CubiqTS.OrderType.LIMIT_CALL) {
      let orderAmt = this.getOrderAmt(order);

      let trackingRate: CubiqTS.BigNumber = null;
      try {
        let trackingRateString: string = null;

        if (ndfMode === true) {
          trackingRateString = this.pricingService.getBlotterNdfTracking(order);
        } else {
          trackingRateString = this.pricingService.getBlotterSpotTracking(order);
        }

        if (
          trackingRateString != null &&
          trackingRateString.length > 0 &&
          trackingRateString != "-"
        ) {
          trackingRate = new CubiqTS.BigNumber(trackingRateString);
        }
      } catch (e) {
        logger.out.warn(
          this.constructor.name +
            ": Failed to convert tracking rate into a big number when calculating VWAP. " +
            e,
        );
      }

      if (orderAmt !== null && order.trackingRt !== null && trackingRate != null) {
        return this.getOrderAmt(order).times(trackingRate);
      } else if (orderAmt !== null && CubiqTS.OrderType.ALGO === order.orderType) {
        let cachedRate = this.rateCache.getRateByKey(this.rateCache.getSpotRateKey(order));
        let midRate = cachedRate != null ? cachedRate.midRate : null;

        if (midRate !== null) return this.getOrderAmt(order).times(new CubiqTS.BigNumber(midRate));
      }

      return null;
    }
    return null;
  }

  private amBuyingBase(order: CubiqTS.OrderDataImpl): boolean {
    if (order.baseCode != order.fixedCode) {
      return order.clientIntent == CubiqTS.OrderIntent.SELL;
    }
    return order.clientIntent == CubiqTS.OrderIntent.BUY;
  }

  private getFormattedRate(vwap: PairVwap, rate: CubiqTS.BigNumber): string {
    let maxRatePrecision = vwap.isFwdTracked ? vwap.fwdRatePrecision : vwap.sptRatePrecision;
    let rounded = rate.decimalPlaces(maxRatePrecision);

    let roundedDp = rounded.decimalPlaces();

    if (roundedDp < vwap.minRatePrecision) {
      return rounded.toFormat(vwap.minRatePrecision);
    } else if (roundedDp < maxRatePrecision) {
      return rounded.toFormat(roundedDp);
    } else {
      return rounded.toFormat(maxRatePrecision);
    }
  }
}
